/** Matches `BANXA.PERSONAL_DETAILS.route` in identityMappingRoutes (constants.js). */
export const PERSONAL_DETAILS_ROUTE_PATH = '/identity/identityConfirmation';

/** Matches `BANXA.TIN` / `BANXA.US_TAX_ID` routes in identityMappingRoutes. */
export const TIN_ROUTE_PATH = '/identity/taxId';

export const PROVIDER_PAYMENT = 'PAYMENT';

const PD_KEYS = ['NAME', 'DOB', 'ADDRESS'];

/**
 * @param {Object|null|undefined} item — `components.items.NAME` (etc.)
 * @returns {boolean}
 */
export function isPaymentProvider(item) {
  if (!item || typeof item !== 'object') {
    return false;
  }
  return String(item.provider || '').toUpperCase() === PROVIDER_PAYMENT;
}

/**
 * True if any NAME / DOB / ADDRESS item is delegated to the payment provider.
 *
 * @param {string|Object|null|undefined} pdValue — raw `PERSONAL_DETAILS` value from queue API
 * @returns {boolean}
 */
export function personalDetailsHasAnyPaymentItem(pdValue) {
  if (pdValue == null || typeof pdValue === 'string') {
    return false;
  }
  if (typeof pdValue !== 'object') {
    return false;
  }
  const items = pdValue.components?.items;
  if (!items || typeof items !== 'object') {
    return false;
  }
  return PD_KEYS.some((k) => {
    if (!Object.prototype.hasOwnProperty.call(items, k)) {
      return false;
    }
    return isPaymentProvider(items[k]);
  });
}

/**
 * True when the PERSONAL_DETAILS step should use `/identity/identityConfirmation`.
 * If **any** granular item is PAYMENT, those fields are collected on the payment screen
 * — skip the PD identity route.
 *
 * @param {string|Object|null|undefined} pdValue — raw `PERSONAL_DETAILS` value from queue API
 * @returns {boolean}
 */
export function personalDetailsHasLocalCollection(pdValue) {
  if (pdValue == null || typeof pdValue === 'string') {
    return true;
  }
  if (typeof pdValue !== 'object') {
    return true;
  }
  const items = pdValue.components?.items;
  if (!items || typeof items !== 'object') {
    return true;
  }

  const presentKeys = PD_KEYS.filter((k) => Object.prototype.hasOwnProperty.call(items, k));

  if (presentKeys.length === 0) {
    return true;
  }

  return !personalDetailsHasAnyPaymentItem(pdValue);
}

/**
 * DOB is still collected as BANXA but the PD identity route was skipped because
 * another field was PAYMENT — collect DOB on the payment page (Primer).
 *
 * @param {Object|null|undefined} queue — raw identity queue object
 * @returns {boolean}
 */
export function collectDobOnPaymentPage(queue) {
  if (!queue || typeof queue !== 'object') {
    return false;
  }
  const pd = queue.PERSONAL_DETAILS;
  if (!pd || typeof pd !== 'object') {
    return false;
  }
  if (personalDetailsHasLocalCollection(pd)) {
    return false;
  }
  const dob = pd.components?.items?.DOB;
  if (!dob || typeof dob !== 'object') {
    return false;
  }
  return !isPaymentProvider(dob);
}

/**
 * TIN should be collected on the payment page when the queue includes TIN and
 * any PD field is delegated to PAYMENT (PD identity route skipped).
 *
 * @param {Object|null|undefined} queue — raw identity queue object
 * @returns {boolean}
 */
export function shouldDeferTinToPayment(queue) {
  if (!queue || typeof queue !== 'object') {
    return false;
  }
  if (!Object.prototype.hasOwnProperty.call(queue, 'TIN') || queue.TIN == null) {
    return false;
  }
  return personalDetailsHasAnyPaymentItem(queue.PERSONAL_DETAILS);
}

/**
 * Removes identity routes that are satisfied by payment / not needed in-app.
 *
 * @param {Array<Object>} newDocuments — route entries from `organizeRoutes`
 * @param {Object} rawQueue — raw queue payload from API
 * @returns {Array<Object>}
 */
export function filterQueueRoutesForPaymentDelegation(newDocuments, rawQueue) {
  if (!Array.isArray(newDocuments) || newDocuments.length === 0) {
    return newDocuments;
  }
  if (!rawQueue || typeof rawQueue !== 'object') {
    return newDocuments;
  }

  const dropPersonalDetails = !personalDetailsHasLocalCollection(rawQueue.PERSONAL_DETAILS);
  const dropTin = shouldDeferTinToPayment(rawQueue);

  return newDocuments.filter((route) => {
    const { url } = route;
    if (dropPersonalDetails && url === PERSONAL_DETAILS_ROUTE_PATH) {
      return false;
    }
    if (dropTin && url === TIN_ROUTE_PATH) {
      return false;
    }
    return true;
  });
}
